import axios from "axios";
import { useEffect, useState, useCallback, useMemo } from "react";
import BannerDeleteModal from "../../../components/AdminSideComponents/banners/BannerDeleteModal";
import BannerList from "../../../components/AdminSideComponents/banners/BannerList";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import { getCloudinaryImageUrl } from "../../../utils/getCloudinaryImageUrl";
import EditBannerModal from "../../../components/AdminSideComponents/banners/EditBannerModal";

const BANNER_API = `${process.env.REACT_APP_API_URL}/banner`;

const BannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [modal, setModal] = useState(null); // "create" | "edit" | "delete"
  const [selectedBanner, setSelectedBanner] = useState(null);

  // ================= FETCH =================
  const fetchBanners = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BANNER_API}/admin`);

      const mapped = (res.data?.data || []).map((b) => ({
        id: b._id,
        title: b.title,
        device: b.screen || "desktop",
        imageUrl: getCloudinaryImageUrl(b.image, { width: 700 }),
        videoUrl: b.video || "",
        link: b.link || "",
        active: b.isActive ?? true,
        sequence: b.sequence ?? 999,
      }));

      setBanners(mapped);
    } catch (err) {
      console.error(err);
      setError("Failed to load banners");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  // ================= DERIVED =================
  const sortedBanners = useMemo(() => {
    return [...banners].sort((a, b) => a.sequence - b.sequence);
  }, [banners]);

  // ================= MODAL =================
  const openModal = (type, banner = null) => {
    setModal(type);
    setSelectedBanner(banner);
  };

  const closeModal = () => {
    setModal(null);
    setSelectedBanner(null);
  };

  // ================= SAVE =================
  const handleSave = async (data) => {
    try {
      if (modal === "edit") {
        const res = await axios.put(`${BANNER_API}/${selectedBanner.id}`, {
          title: data.title,
          screen: data.device,
          image: data.imageUrl,
          originalImage: data.imageUrl,
          video: data.videoUrl,
          link: data.link,
          isActive: data.active,
          sequence: data.sequence, // ✅ FIXED (was wrong before)
        });

        const updated = res.data?.data;

        if (updated) {
          setBanners((prev) =>
            prev.map((b) =>
              b.id === updated._id
                ? {
                  ...b,
                  title: updated.title,
                  device: updated.screen,
                  imageUrl: getCloudinaryImageUrl(updated.image, {
                    width: 700,
                  }),
                  videoUrl: updated.video || "",
                  link: updated.link || "",
                  active: updated.isActive,
                  sequence: updated.sequence,
                }
                : b
            )
          );
        }
      }

      if (modal === "create") {
        const res = await axios.post(`${BANNER_API}/create`, {
          title: data.title,
          screen: data.device,
          image: data.imageUrl,
          originalImage: data.imageUrl,
          video: data.videoUrl,
          link: data.link,
          isActive: data.active,
          sequence: data.sequence, // ✅ now dynamic
        });

        const created = res.data?.data;

        if (created) {
          setBanners((prev) => [
            ...prev,
            {
              id: created._id,
              title: created.title,
              device: created.screen,
              imageUrl: getCloudinaryImageUrl(created.image, {
                width: 700,
              }),
              videoUrl: created.video || "",
              link: created.link || "",
              active: created.isActive,
              sequence: created.sequence,
            },
          ]);
        }
      }

      closeModal();
    } catch (err) {
      console.error(err);
      setError("Save failed");
    }
  };

  // ================= DELETE =================
  const handleDelete = async () => {
    try {
      await axios.delete(`${BANNER_API}/${selectedBanner.id}`);
      setBanners((prev) =>
        prev.filter((b) => b.id !== selectedBanner.id)
      );
      closeModal();
    } catch (err) {
      console.error(err);
      setError("Delete failed");
    }
  };

  // ================= UI =================
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF7F3] to-gray-50 px-4 md:px-6">

      {/* HEADER */}
      <div className="py-4">
        <Breadcrumb
          title1="Banners"
          title2="Banner Management"
          showBtn
          buttonName="Add Banner"
          onBtnClick={() => openModal("create")}
        />
      </div>

      {/* CONTENT */}
      <div className="bg-white rounded-xl shadow-md p-4 md:p-6">
        {error && (
          <div className="mb-4 text-sm text-red-500">{error}</div>
        )}

        {loading ? (
          <div className="py-10 text-center text-gray-500">
            Loading banners...
          </div>
        ) : (
          <BannerList
            banners={sortedBanners}
            onEdit={(b) => openModal("edit", b)}
            onDelete={(b) => openModal("delete", b)}
          />
        )}
      </div>

      <EditBannerModal
        isOpen={modal === "create" || modal === "edit"}
        onClose={closeModal}
        banner={modal === "edit" ? selectedBanner : null}
        onSave={handleSave}
      />

      {/* DELETE */}
      <BannerDeleteModal
        isOpen={modal === "delete"}
        onClose={closeModal}
        banner={selectedBanner}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default BannerManagement;